/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useState } from 'react'
import { ColumnInputProps, Option } from '../InputInterface';
import { Button, FormGroup, Input, InputGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Select, { ActionMeta, MenuListProps, OnChangeValue, components, GroupBase, StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { loadValueListById, ValueList, valueListSelectors } from '../../../../../../slice/valueListSlice';
import { useDispatch } from 'react-redux';
import { initCustomOptions, ReactSelectOptions } from '../../selectUtils';
import { useAppSelector } from '../../../../../../reducers';

const reactSelectStyles: StylesConfig<ReactSelectOptions, any, GroupBase<ReactSelectOptions>> = {
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#2971e5',
    color: 'white',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'white'
  }),
  placeholder: (base) => ({
    ...base,
    borderColor: '#6a6c70',
    color: '#6a6c70'
  }),
  option: (base) => ({
    ...base,
    ':hover': {
      backgroundColor: '#adcdf2'
    }
  })
};

interface SelectComponentParams {
  options: ReactSelectOptions[],
  value: ReactSelectOptions[],
  [key: string]: any
}

const CustomMenuListCreateModal = ({ toggle, addOption }: { toggle: () => void, addOption: (option: string) => void }) => {

  const [value, setValue] = useState<string>('');
  const handleSubmit = () => {
    if (value) {
      addOption(value);
      toggle();
    }
  }

  return (
    <Modal isOpen={true} toggle={toggle} backdrop='static'>
      <ModalHeader toggle={toggle}>Create a new option</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>New option name</Label>
          <Input onChange={e => setValue(e.target.value)} placeholder='Type your option here...' />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color='link' onClick={toggle}>cancel</Button>
        <Button disabled={value === ''} onClick={() => handleSubmit()}>Add option</Button>
      </ModalFooter>
    </Modal>
  );
}

interface CustomMenuListProps {
  setShowCreateModal: (show: boolean) => void;
}

const CustomMenuList = (
  props: MenuListProps<ReactSelectOptions, boolean, GroupBase<ReactSelectOptions>> & CustomMenuListProps
) => {
  const { setShowCreateModal } = props;

  return (
    <>
      <components.MenuList {...props}>
        {props.children}
        <div className='create-new-option px-3 py-2 text-ThemeTextPlaceholder' style={{ cursor: 'pointer' }} onClick={() => setShowCreateModal(true)}>"Click to create a new option"</div>
      </components.MenuList>
    </>
  );
};

const SelectComponent = (props: SelectComponentParams) => {
  const { placeholder, allowCustomOptions, disabled, handleChange, value, options, setOptions, multi = false } = props;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const isMultipleChoice = multi;

  const onChangeHandler = (newValue: OnChangeValue<ReactSelectOptions, boolean>, actionMeta: ActionMeta<ReactSelectOptions>) => {
    if (newValue === null) {
      return;
    }
    if (Array.isArray(newValue)) {
      if (actionMeta.action === 'create-option') {
        setOptions(initCustomOptions(newValue.map(o => o.value), options, allowCustomOptions));
      }
      handleChange(newValue.map(v => v.value));
      return;
    }

    if (typeof newValue === 'object' && newValue !== null) {
      const { value } = newValue as ReactSelectOptions;
      if (actionMeta.action === 'create-option') {
        setOptions(initCustomOptions([value], options, allowCustomOptions));
      }
      handleChange([value]);
    }
  }

  if (allowCustomOptions) {
    const onAddOption = (option: string) => onChangeHandler(
      { label: option, value: option },
      { action: 'create-option', option: { label: option, value: option } }
    );
    return <>
      {showCreateModal ? <CustomMenuListCreateModal addOption={onAddOption} toggle={() => setShowCreateModal(false)} /> : null}
      <CreatableSelect
        {...props}
        placeholder={placeholder}
        className='w-100 valueList-react-select'
        isMulti={isMultipleChoice}
        onChange={onChangeHandler}
        value={(isMultipleChoice ? value : value[0]) ?? null}
        styles={reactSelectStyles}
        isSearchable={true}
        isDisabled={disabled}
        createOptionPosition={'first'}
        components={{ MenuList: (props) => CustomMenuList({ ...props, setShowCreateModal }) }}
        options={options}
      />
    </>
  }

  return <Select
    placeholder={placeholder}
    className='w-100 valueList-react-select'
    isMulti={isMultipleChoice}
    onChange={onChangeHandler}
    value={(isMultipleChoice ? value : value[0]) ?? null}
    styles={reactSelectStyles}
    isSearchable={true}
    isDisabled={disabled}
    options={options}
  />
}

const getOptions = (options: Option[] | undefined) => (options ?? [])
  .map((o) => ({ value: o.code, label: o.name }));

const renderInputGroup = (label: any, content: any) => (
  <FormGroup>
    {label}
    <InputGroup className='mb-3'>
      {content}
    </InputGroup>
  </FormGroup>
);

const SelectInput = (props: ColumnInputProps) => {
  const {
    isDisabled,
    updateColumn,
    column,
    label,
    inputColumn,
    placeholder
  } = props;

  const isMultipleChoice = props.multi === true;
  const allowCustomOptions = !!column.validation?.allowCustomOptions;

  const handleChange = (newValues: string[]) => {
    updateColumn({
      code: column.code,
      value: isMultipleChoice ? newValues : (newValues[0] === '' ? undefined : newValues[0])
    });
  }

  const dispatch = useDispatch();
  const valueList: ValueList | undefined = useAppSelector((state) => valueListSelectors.selectById(state, column.listId || ''));

  const [options, setOptions] = useState<ReactSelectOptions[]>([])

  React.useEffect(() => {
    // deals with initial options set
    setOptions(valueList ? getOptions(valueList.options) : getOptions(column.options))
    if (column.listId) {
      if (valueList?.loading || valueList?.loaded) {
        return;
      }
      dispatch(loadValueListById(column.listId));
    }
  }, [dispatch, column, valueList]);

  React.useEffect(() => {
    // deals with adding custom option from column value on edit
    if (!inputColumn.value || !options.length) {
      return
    }
    const inputExist = options.find(
      option => Array.isArray(inputColumn.value) ? inputColumn.value?.includes(String(option.value)) : option.value === inputColumn.value
    )
    if (inputExist) {
      return
    }
    setOptions(initCustomOptions(
      Array.isArray(inputColumn.value) ? inputColumn.value : [String(inputColumn.value)],
      options,
      allowCustomOptions
    ))
  }, [inputColumn, allowCustomOptions, options])

  if (column.listId) {
    if (!valueList || !valueList.loaded) {
      return renderInputGroup(label, <>Loading options...</>)
    }

    if (valueList.error) {
      return renderInputGroup(label, <span>Error: loading options</span>)
    }
  }

  const selectedOptions: ReactSelectOptions[] = options
    .filter(option => !option.isDisabled)
    .filter(option => Array.isArray(inputColumn.value) ? inputColumn.value.includes(String(option.value)) : inputColumn.value === option.value)

  const disabled = isDisabled?.(props);

  return renderInputGroup(
    label,
    <SelectComponent
      placeholder={placeholder}
      disabled={disabled}
      options={options}
      setOptions={setOptions}
      value={selectedOptions}
      allowCustomOptions={allowCustomOptions}
      multi={isMultipleChoice}
      handleChange={handleChange} />
  );
}

export default SelectInput;
